/**
 * 关键词弹窗组件
 * 当对话中检测到关键词时，在左边角落显示滚动的图片序列缩略图
 * 支持多个关键词同时触发显示多个缩略图
 * 点击缩略图可优雅展开为大屏查看，大屏模式下可通过标签栏切换不同图片组
 */

import React, { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import { IconClose, IconLeft, IconRight } from '@arco-design/web-react/icon';
import styles from './index.module.less';

export interface KeywordPopupConfig {
  keywords: string[];
  /** @deprecated 使用 images 数组代替 */
  image?: string;
  /** 图片序列，支持多张图片轮播 */
  images?: string[];
  title?: string;
  description?: string;
  link?: string;
}

interface KeywordPopupProps {
  /** 当前已触发显示的弹窗配置（用于缩略图列表） */
  configs: KeywordPopupConfig[];
  /** 所有可用的弹窗配置（用于大屏模式切换，可选，默认等于configs） */
  allConfigs?: KeywordPopupConfig[];
  onClose: (title?: string) => void;
  onCloseAll?: () => void;
}

// 处理图片URL（增加空值保护）
const getImageUrl = (src: string | undefined | null): string => {
  if (!src) return '';
  return src.startsWith('http') ? src : `/${src}`;
};

// 获取配置中的图片数组（兼容旧的单图片配置）
const getImages = (config: KeywordPopupConfig): string[] => {
  if (config.images && config.images.length > 0) {
    return config.images;
  }
  if (config.image) {
    return [config.image];
  }
  return [];
};

// 单个缩略图组件
function ThumbnailCard({
  config,
  index,
  onExpand,
  onClose,
}: {
  config: KeywordPopupConfig;
  index: number;
  onExpand: (config: KeywordPopupConfig) => void;
  onClose: (title?: string) => void;
}) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [loadedImages, setLoadedImages] = useState<Set<number>>(new Set());
  const [errorImages, setErrorImages] = useState<Set<number>>(new Set());
  const [isClosing, setIsClosing] = useState(false);
  const autoPlayRef = useRef<NodeJS.Timeout | null>(null);

  const images = useMemo(() => getImages(config), [config]);

  // 自动轮播
  useEffect(() => {
    if (images.length <= 1) return;

    autoPlayRef.current = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % images.length);
    }, 3000);

    return () => {
      if (autoPlayRef.current) {
        clearInterval(autoPlayRef.current);
      }
    };
  }, [images.length]);

  const handleClose = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      setIsClosing(true);
      setTimeout(() => {
        onClose(config.title);
      }, 300);
    },
    [onClose, config.title]
  );

  const handleImageLoad = useCallback((idx: number) => {
    setLoadedImages((prev) => new Set(prev).add(idx));
  }, []);

  const handleImageError = useCallback((idx: number) => {
    setErrorImages((prev) => new Set(prev).add(idx));
    setLoadedImages((prev) => new Set(prev).add(idx));
  }, []);

  if (images.length === 0) return null;

  const currentImage = images[currentIndex];
  const isCurrentLoaded = loadedImages.has(currentIndex);
  const isCurrentError = errorImages.has(currentIndex);

  return (
    <div
      className={`${styles.thumbnailCard} ${isClosing ? styles.closing : ''}`}
      style={{ animationDelay: `${index * 0.1}s` }}
      onClick={() => onExpand(config)}
      role="button"
      tabIndex={0}
      aria-label={`点击查看 ${config.title || '图片'}`}
    >
      <button className={styles.thumbnailCloseBtn} onClick={handleClose} aria-label="关闭">
        <IconClose />
      </button>

      <div className={styles.thumbnailImageWrapper}>
        {!isCurrentLoaded && !isCurrentError && (
          <div className={styles.thumbnailLoading}>
            <div className={styles.loadingSpinnerSmall} />
          </div>
        )}
        {isCurrentError ? (
          <div className={styles.thumbnailError}>加载失败</div>
        ) : (
          <img
            src={getImageUrl(currentImage)}
            alt={config.title || `图片 ${currentIndex + 1}`}
            className={styles.thumbnailImage}
            onLoad={() => handleImageLoad(currentIndex)}
            onError={() => handleImageError(currentIndex)}
          />
        )}
      </div>

      <div className={styles.thumbnailInfo}>
        {config.title && <span className={styles.thumbnailTitle}>{config.title}</span>}
        {images.length > 1 && (
          <span className={styles.thumbnailCount}>
            {currentIndex + 1}/{images.length}
          </span>
        )}
      </div>

      {images.length > 1 && (
        <div className={styles.thumbnailIndicators}>
          {images.map((_, idx) => (
            <span
              key={idx}
              className={`${styles.thumbnailDot} ${idx === currentIndex ? styles.active : ''}`}
            />
          ))}
        </div>
      )}
    </div>
  );
}

// 大屏展开模式组件
function ExpandedViewer({
  configs,
  initialConfig,
  onClose,
}: {
  configs: KeywordPopupConfig[];
  initialConfig: KeywordPopupConfig;
  onClose: () => void;
}) {
  // 当前选中的图片组索引
  const [activeGroupIndex, setActiveGroupIndex] = useState(() =>
    configs.findIndex((c) => c.title === initialConfig.title)
  );
  // 当前图片索引
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [loadedImages, setLoadedImages] = useState<Set<string>>(new Set());
  const [errorImages, setErrorImages] = useState<Set<string>>(new Set());

  // 确保 activeGroupIndex 在有效范围内
  const safeGroupIndex = activeGroupIndex >= 0 && activeGroupIndex < configs.length
    ? activeGroupIndex
    : 0;
  const activeConfig = configs[safeGroupIndex];
  const images = useMemo(() => (activeConfig ? getImages(activeConfig) : []), [activeConfig]);

  // 切换图片组时重置图片索引
  useEffect(() => {
    setCurrentImageIndex(0);
  }, [safeGroupIndex]);

  // 确保 currentImageIndex 在有效范围内
  const safeImageIndex = images.length > 0
    ? Math.min(currentImageIndex, images.length - 1)
    : 0;

  const handleImageLoad = useCallback((key: string) => {
    setLoadedImages((prev) => new Set(prev).add(key));
  }, []);

  const handleImageError = useCallback((key: string) => {
    setErrorImages((prev) => new Set(prev).add(key));
    setLoadedImages((prev) => new Set(prev).add(key));
  }, []);

  const handlePrev = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      setCurrentImageIndex((prev) => (prev - 1 + images.length) % images.length);
    },
    [images.length]
  );

  const handleNext = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      setCurrentImageIndex((prev) => (prev + 1) % images.length);
    },
    [images.length]
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'ArrowLeft') {
        setCurrentImageIndex((prev) => (prev - 1 + images.length) % images.length);
      } else if (e.key === 'ArrowRight') {
        setCurrentImageIndex((prev) => (prev + 1) % images.length);
      } else if (e.key === 'Escape') {
        onClose();
      }
    },
    [images.length, onClose]
  );

  const handleImageClick = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    if (activeConfig.link) {
      window.open(activeConfig.link, '_blank');
    }
  }, [activeConfig.link]);

  // 点击背景区域退出
  const handleOverlayClick = useCallback((e: React.MouseEvent) => {
    // 只有点击 overlay 本身时才关闭
    if (e.target === e.currentTarget) {
      onClose();
    }
  }, [onClose]);

  // 如果没有配置或图片，不渲染
  if (!activeConfig || images.length === 0) return null;

  const currentImage = images[safeImageIndex];
  const imageKey = `${safeGroupIndex}-${safeImageIndex}`;
  const isCurrentLoaded = loadedImages.has(imageKey);
  const isCurrentError = errorImages.has(imageKey);

  return (
    <div className={styles.expandedOverlay} onClick={handleOverlayClick}>
      <div
        className={styles.expandedContainer}
        onKeyDown={handleKeyDown}
        tabIndex={0}
        role="dialog"
        aria-label="图片查看器"
        onClick={(e) => e.stopPropagation()}
      >

        {/* 主图片区域 */}
        <div
          className={styles.expandedImageWrapper}
          onClick={activeConfig.link ? handleImageClick : undefined}
          style={{ cursor: activeConfig.link ? 'pointer' : 'default' }}
        >
          {!isCurrentLoaded && !isCurrentError && <div className={styles.loadingSpinner} />}
          {isCurrentError ? (
            <div className={styles.imageError}>图片加载失败</div>
          ) : (
            <img
              key={imageKey}
              src={getImageUrl(currentImage)}
              alt={activeConfig.title || `图片 ${currentImageIndex + 1}`}
              className={styles.expandedImage}
              onLoad={() => handleImageLoad(imageKey)}
              onError={() => handleImageError(imageKey)}
            />
          )}
        </div>

        {/* 导航按钮 */}
        {images.length > 1 && (
          <>
            <button
              className={`${styles.navBtn} ${styles.prevBtn}`}
              onClick={handlePrev}
              aria-label="上一张"
            >
              <IconLeft />
            </button>
            <button
              className={`${styles.navBtn} ${styles.nextBtn}`}
              onClick={handleNext}
              aria-label="下一张"
            >
              <IconRight />
            </button>
          </>
        )}

        {/* 底部信息和指示器 */}
        <div className={styles.expandedFooter}>
          {(activeConfig.title || activeConfig.description) && (
            <div className={styles.expandedInfo}>
              {activeConfig.title && <h3 className={styles.expandedTitle}>{activeConfig.title}</h3>}
              {activeConfig.description && (
                <p className={styles.expandedDescription}>{activeConfig.description}</p>
              )}
            </div>
          )}
          {images.length > 1 && (
            <div className={styles.indicators}>
              {images.map((_, index) => (
                <button
                  key={index}
                  className={`${styles.indicator} ${index === currentImageIndex ? styles.active : ''}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    setCurrentImageIndex(index);
                  }}
                  aria-label={`查看第 ${index + 1} 张图片`}
                />
              ))}
            </div>
          )}

          {/* 底部标签栏 - 多个图片组时显示 */}
          {configs.length > 1 && (
            <div className={styles.tabBar}>
              {configs.map((config, idx) => (
                <button
                  key={config.title || idx}
                  className={`${styles.tabItem} ${idx === activeGroupIndex ? styles.active : ''}`}
                  onClick={() => setActiveGroupIndex(idx)}
                >
                  {config.title || `图片组 ${idx + 1}`}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* 右下角返回按钮 */}
        <button className={styles.backBtn} onClick={onClose}>
          <IconClose />
          <span>返回</span>
        </button>
      </div>
    </div>
  );
}

export default function KeywordPopup({ configs, allConfigs, onClose, onCloseAll }: KeywordPopupProps) {
  const [expandedConfig, setExpandedConfig] = useState<KeywordPopupConfig | null>(null);

  // 大屏模式使用所有配置（如果提供），否则使用已触发的配置
  const expandedConfigs = allConfigs && allConfigs.length > 0 ? allConfigs : configs;

  // 重置展开状态
  useEffect(() => {
    if (configs.length === 0) {
      setExpandedConfig(null);
    }
  }, [configs.length]);

  const handleExpand = useCallback((config: KeywordPopupConfig) => {
    setExpandedConfig(config);
  }, []);

  const handleCloseExpanded = useCallback(() => {
    setExpandedConfig(null);
  }, []);

  const handleCloseThumbnail = useCallback(
    (title?: string) => {
      onClose(title);
    },
    [onClose]
  );

  if (configs.length === 0) {
    return null;
  }

  return (
    <>
      {/* 左侧缩略图列表 */}
      <div className={styles.thumbnailContainer}>
        {configs.map((config, index) => (
          <ThumbnailCard
            key={config.title || index}
            config={config}
            index={index}
            onExpand={handleExpand}
            onClose={handleCloseThumbnail}
          />
        ))}
      </div>

      {/* 大屏展开模式 - 使用所有配置以支持切换到未触发的图片集 */}
      {expandedConfig && (
        <ExpandedViewer
          configs={expandedConfigs}
          initialConfig={expandedConfig}
          onClose={handleCloseExpanded}
        />
      )}
    </>
  );
}
